// The Books reads as queries. Every key sits under `['books', orgRef]`, so one invalidation covers
// an organization, and switching organizations never shows the old one's late answer.
import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { decodeBooksHome } from './books-decode';
import { refusalOf } from './books-state';
import {
  fetchBooks,
  fetchBooksAccountActivity,
  fetchBooksJournal,
  fetchBooksReconciliation,
  fetchBooksStatement,
  type StatementQuery,
} from './books-transport';

/**
 * The order a ledger walk reads in. The pages walk oldest first only; the backend's newest-first and
 * start-at-a-month walks (PT-BK24) reach them with BM-WEB2 part 3, as values here and parameters on
 * the read.
 */
export type BooksWalk = 'oldest_first';

/** Whether a walk's page was refused for its cursor (`BOOKS_CURSOR_INVALID`). */
export function isCursorRefused(error: unknown): boolean {
  return refusalOf(error).code === 'BOOKS_CURSOR_INVALID';
}

/**
 * "Show more" for a walk. The cursor is the backend's, carried unchanged; one the backend no longer
 * continues (`BOOKS_CURSOR_INVALID`: a cursor taken before Books' engine changed under the walk)
 * restarts the walk from its first page with fresh cursors, as deep as it had read, rather than
 * ending it. `restarting` holds the pages already shown on screen meanwhile.
 */
export function useShowMore(walk: {
  fetchNextPage: () => Promise<{ isError: boolean; error: unknown }>;
  refetch: () => Promise<unknown>;
}) {
  const [restarting, setRestarting] = useState(false);
  const showMore = async () => {
    const next = await walk.fetchNextPage();
    if (!next.isError || !isCursorRefused(next.error)) return;
    setRestarting(true);
    try {
      await walk.refetch();
    } finally {
      setRestarting(false);
    }
  };
  return { showMore, restarting };
}

export function useBooksHome(orgRef: string | null) {
  return useQuery({
    queryKey: ['books', orgRef, 'home'],
    queryFn: async ({ signal }) => decodeBooksHome(await fetchBooks(orgRef!, signal)),
    enabled: orgRef !== null,
    retry: false,
    retryOnMount: false,
  });
}

export function useBooksStatement(orgRef: string, statement: string, query: StatementQuery | null) {
  return useQuery({
    queryKey: ['books', orgRef, 'statement', statement, query],
    queryFn: ({ signal }) => fetchBooksStatement(orgRef, statement, query!, signal),
    enabled: query !== null,
    retry: false,
  });
}

export function useBooksJournal(orgRef: string, walk: BooksWalk) {
  return useInfiniteQuery({
    queryKey: ['books', orgRef, 'journal', walk],
    queryFn: ({ pageParam, signal }) => fetchBooksJournal(orgRef, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
  });
}

export function useBooksAccount(orgRef: string, accountCode: string, walk: BooksWalk) {
  return useInfiniteQuery({
    queryKey: ['books', orgRef, 'account', accountCode, walk],
    queryFn: ({ pageParam, signal }) =>
      fetchBooksAccountActivity(orgRef, accountCode, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
  });
}

export function useBooksReconciliation(orgRef: string, period: string | null) {
  return useQuery({
    queryKey: ['books', orgRef, 'reconciliation', period],
    queryFn: ({ signal }) => fetchBooksReconciliation(orgRef, period!, signal),
    enabled: period !== null,
    retry: false,
  });
}
